import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ErrorNote } from '../components/ui';

export function NewReportPage() {
  const navigate = useNavigate();
  const [type, setType] = useState<'monthly' | 'travel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const thisMonth = new Date().toISOString().slice(0, 7);
  const [periodMonth, setPeriodMonth] = useState(thisMonth);
  const [title, setTitle] = useState('');
  const [trip, setTrip] = useState({
    name: '', destination: '', purpose: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = type === 'monthly'
        ? { type, periodMonth, title: title || monthLabel(periodMonth) }
        : { type: 'travel' as const, title: title || trip.name || 'Travel report', trip };
      const { report } = await api.post<{ report: { id: string } }>('/reports', body);
      navigate(`/reports/${report.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the report');
    } finally {
      setBusy(false);
    }
  }

  if (!type) {
    return (
      <div className="mx-auto max-w-lg">
        <h1 className="mb-4 text-xl font-semibold">New report</h1>
        <div className="grid gap-3">
          <button type="button" onClick={() => setType('monthly')} className="card p-5 text-left active:bg-slate-50">
            <p className="text-lg font-medium">Monthly expenses</p>
            <p className="mt-1 text-sm text-slate-500">
              Routine spend for a calendar month — supplies, software, phone, client meals.
            </p>
          </button>
          <button type="button" onClick={() => setType('travel')} className="card p-5 text-left active:bg-slate-50">
            <p className="text-lg font-medium">Travel / trip</p>
            <p className="mt-1 text-sm text-slate-500">
              A single trip, with mileage and per diem alongside the receipts.
            </p>
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-lg space-y-4">
      <h1 className="text-xl font-semibold">{type === 'monthly' ? 'Monthly report' : 'Travel report'}</h1>

      {type === 'monthly' ? (
        <div>
          <label className="label" htmlFor="period">Month</label>
          {/* Native month picker: far better than any JS control on a phone. */}
          <input id="period" type="month" className="field" value={periodMonth}
                 onChange={(e) => setPeriodMonth(e.target.value)} required />
        </div>
      ) : (
        <>
          <div>
            <label className="label" htmlFor="tname">Trip name</label>
            <input id="tname" className="field" value={trip.name} required
                   placeholder="Boston trade show"
                   onChange={(e) => setTrip({ ...trip, name: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="dest">Destination</label>
            <input id="dest" className="field" value={trip.destination} required
                   placeholder="Boston, MA"
                   onChange={(e) => setTrip({ ...trip, destination: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="start">Start</label>
              <input id="start" type="date" className="field" value={trip.startDate} required
                     onChange={(e) => setTrip({ ...trip, startDate: e.target.value })} />
            </div>
            <div>
              <label className="label" htmlFor="end">End</label>
              <input id="end" type="date" className="field" value={trip.endDate} required
                     onChange={(e) => setTrip({ ...trip, endDate: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="purpose">Purpose (optional)</label>
            <input id="purpose" className="field" value={trip.purpose}
                   onChange={(e) => setTrip({ ...trip, purpose: e.target.value })} />
          </div>
        </>
      )}

      <div>
        <label className="label" htmlFor="title">Title (optional)</label>
        <input id="title" className="field" value={title} onChange={(e) => setTitle(e.target.value)}
               placeholder={type === 'monthly' ? monthLabel(periodMonth) : trip.name || 'Travel report'} />
      </div>

      <ErrorNote>{error}</ErrorNote>

      <div className="flex gap-2">
        <button type="button" className="btn-secondary flex-1" onClick={() => setType(null)}>Back</button>
        <button type="submit" className="btn-primary flex-1" disabled={busy}>
          {busy ? 'Creating…' : 'Create report'}
        </button>
      </div>
    </form>
  );
}

function monthLabel(period: string): string {
  const [y, m] = period.split('-');
  if (!y || !m) return period;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
